import { Resources } from 'constants/resources';

import { Children, isValidElement, useCallback } from 'react';

import clsx from 'clsx';
import { usePermissions } from 'hooks/usePermissions';
import {
  DatagridClasses,
  DatagridHeaderCell,
  DatagridHeaderProps,
  useListContext,
} from 'react-admin';
import { Checkbox } from 'shared/mui/Checkbox/Checkbox';
import { TableCell, TableHead, TableRow } from 'shared/mui/Table';
import { Permission } from 'types/embedded/permission';

export const BankOperationsHeaderRow = ({
  children,
  className,
  hasBulkActions = false,
  isRowSelectable,
}: DatagridHeaderProps) => {
  const { sort, data = [], onSelect, selectedIds = [], setSort } = useListContext();
  const { hasResourcePermissions } = usePermissions();

  const isSelectable = useCallback(
    (record: any) => (isRowSelectable ? isRowSelectable(record) : true),
    [isRowSelectable],
  );

  const selectableIds = data.filter(isSelectable).map((record) => record.id);

  const isAllSelected =
    selectedIds.length > 0 &&
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.includes(id));

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const idsToSelect = checked
        ? data
            .filter((record) => !selectedIds.includes(record.id))
            .filter(isSelectable)
            .map((record) => record.id)
        : [];

      onSelect(checked ? selectedIds.concat(idsToSelect) : []);
    },
    [data, selectedIds, onSelect, isSelectable],
  );

  const handleSort = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      const field = event.currentTarget.dataset.field!;
      const currentOrder = sort.field === field ? sort.order : undefined;
      const newOrder = currentOrder === 'ASC' ? 'DESC' : 'ASC';

      setSort({ field, order: newOrder });
    },
    [sort, setSort],
  );

  return (
    <TableHead className={clsx(className, DatagridClasses.thead)}>
      <TableRow className={clsx(DatagridClasses.row, DatagridClasses.headerRow)}>
        {hasBulkActions && (
          <TableCell className={DatagridClasses.headerCell} padding="checkbox">
            <Checkbox
              checked={isAllSelected}
              className="select-all"
              color="primary"
              disabled={
                !hasResourcePermissions(Resources.RequestBankOperations, [
                  Permission.Delete,
                  Permission.Full,
                ])
              }
              onChange={handleSelectAll}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
        )}

        {Children.map(children, (child, index) => {
          if (!isValidElement(child)) {
            return null;
          }

          const { source, sortBy } = child.props as any;
          const sortField = sortBy || source;
          const isSorting = sort.field === sortField;

          return (
            <DatagridHeaderCell
              className={clsx(DatagridClasses.headerCell, `column-${source}`)}
              field={child}
              isSorting={isSorting}
              key={source || index}
              sort={sort}
              updateSort={handleSort}
            />
          );
        })}
      </TableRow>
    </TableHead>
  );
};
